import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import Seo from "@/components/Seo";

export default function InfoPage() {
  const { section } = useParams();
  const [block, setBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang } = useI18n();

  useEffect(() => {
    setLoading(true);
    api.get(`/content-blocks/${section}`).then((r) => setBlock(r.data)).catch(() => setBlock(null)).finally(() => setLoading(false));
  }, [section]);

  function tr(field, fallback = "") {
    if (!block) return fallback;
    return block?.i18n?.[lang]?.[field] ?? block?.[field] ?? fallback;
  }

  function trItem(item, field, fallback = "") {
    return item?.i18n?.[lang]?.[field] ?? item?.[field] ?? fallback;
  }

  return (
    <SiteLayout>
      {block && <Seo title={`${tr("title")} · Berdhill Rooms`} description={tr("subtitle")} />}
      <section className="pt-32 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/" data-testid="info-back" className="text-sm text-brand-muted hover:text-brand-text inline-flex items-center gap-1 mb-6">
            <ArrowLeft size={14} /> Back
          </Link>

          {loading && <div className="text-brand-muted">Loading…</div>}

          {!loading && !block && (
            <div data-testid="info-not-found">
              <h1 className="font-display text-4xl mb-3">Section not found</h1>
              <p className="text-brand-muted">This information section is not available.</p>
            </div>
          )}

          {!loading && block && (
            <div data-testid={`info-block-${block.section_key}`}>
              <div className="mb-10 border-b border-brand-border pb-6">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-muted">
                  {tr("subtitle")}
                </span>
                <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("title")}</h1>
              </div>

              <div className="space-y-6">
                {(block.items || []).map((item, i) => (
                  <article key={i} data-testid={`info-item-${i}`} className="group bg-white border border-brand-border rounded-md p-6 hover:shadow-sm transition-shadow">
                    <h2 className="font-display text-xl md:text-2xl text-brand-text">
                      {trItem(item, "title")}
                    </h2>
                    {trItem(item, "content") && (
                      <p className="mt-2 text-brand-muted leading-relaxed text-sm md:text-base whitespace-pre-line">
                        {trItem(item, "content")}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
